import React, { useEffect, useRef, useLayoutEffect, useState, memo } from 'react';
import { 
  Trash2, 
  Copy, 
  Lock,
  Unlock,
  Settings2,
  FolderOpen,
  Layers
} from 'lucide-react';

interface NodeContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleLock: () => void;
  onOpenProperties: () => void;
  onOpenInside: () => void;
  isLocked: boolean;
  hasNestedArchitecture: boolean;
  nestedComponentCount?: number;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  x,
  y,
  onClose,
  onDelete,
  onDuplicate,
  onToggleLock,
  onOpenProperties,
  onOpenInside,
  isLocked,
  hasNestedArchitecture,
  nestedComponentCount = 0
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState({ x, y });

  // Adjust position to stay within viewport
  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newX = x;
      let newY = y;

      // Check right edge
      if (x + rect.width > viewportWidth) {
        newX = x - rect.width;
      }

      // Check bottom edge
      if (y + rect.height > viewportHeight) {
        newY = y - rect.height;
      }
      
      setAdjustedPos({ x: newX, y: newY });
    }
  }, [x, y]);

  // Close if clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Add small delay to prevent immediate closing from the triggering click
    const timer = setTimeout(() => {
        document.addEventListener('click', handleClick);
        document.addEventListener('contextmenu', handleClick);
    }, 50);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('contextmenu', handleClick);
    };
  }, [onClose]); // Only depend on onClose, not x/y/hasNestedArchitecture

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!menuRef.current) return;
      
      // Close on Escape
      if (e.key === 'Escape') {
        onClose();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      style={{ 
        top: adjustedPos.y, 
        left: adjustedPos.x,
        transformOrigin: 'top left',
        animationDuration: '150ms',
      }}
      className="absolute z-[100] bg-white border-2 border-gray-200 rounded-xl shadow-2xl min-w-[240px] py-2 animate-scale-in will-change-transform overflow-hidden"
      onContextMenu={(e) => e.preventDefault()}
      role="menu"
      aria-label="Component options menu"
    >
      {/* Header */}
      <div className="px-4 py-2.5 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
        <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">
          Component Options
        </div>
      </div>

      {/* Primary Action - Open Inside */}
      <div className="px-2 pt-2 pb-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenInside();
          }}
          className="w-full text-left px-3 py-2.5 text-sm bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 hover:from-blue-100 hover:via-purple-100 hover:to-blue-100 text-blue-700 hover:text-blue-800 flex items-center gap-2.5 transition-all duration-150 active:scale-95 font-semibold rounded-lg border border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md"
          title="Open and design the internal architecture of this component. You can add sub-components inside."
          aria-label="Open component to design its internal architecture"
        >
          <FolderOpen size={18} className="text-blue-600 flex-shrink-0" />
          <span className="flex-1">Open Inside</span>
          {hasNestedArchitecture && nestedComponentCount > 0 && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow-sm">
              {nestedComponentCount}
            </span>
          )}
        </button>
      </div>

      <div className="my-2 border-t border-gray-200"></div>

      {/* Standard Actions */}
      <div className="px-2 space-y-0.5">
        <button
          onClick={onOpenProperties}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-2.5 transition-all duration-150 active:scale-95 rounded-md"
          title="Open properties panel to configure component settings"
          aria-label="Open properties panel"
        >
          <Settings2 size={16} className="text-gray-500 flex-shrink-0" />
          <span className="flex-1">Properties</span>
        </button>

        <button
          onClick={onDuplicate}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-2.5 transition-all duration-150 active:scale-95 rounded-md"
          title="Create a copy of this component (Ctrl+G to group)"
          aria-label="Duplicate this component"
        >
          <Copy size={16} className="text-gray-500 flex-shrink-0" />
          <span className="flex-1">Duplicate</span>
        </button>

        <button
          onClick={onToggleLock}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-2.5 transition-all duration-150 active:scale-95 rounded-md"
          title={isLocked ? 'Unlock component to allow movement' : 'Lock component to prevent movement'}
          aria-label={isLocked ? 'Unlock component position' : 'Lock component position'}
        >
          {isLocked ? (
            <Unlock size={16} className="text-amber-500 flex-shrink-0" />
          ) : (
            <Lock size={16} className="text-gray-500 flex-shrink-0" />
          )}
          <span className="flex-1">{isLocked ? 'Unlock' : 'Lock'}</span>
        </button>
      </div>

      <div className="my-2 border-t border-gray-200"></div>

      {/* Destructive Action */}
      <div className="px-2 pb-2">
        <button
          onClick={onDelete}
          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 flex items-center gap-2.5 transition-all duration-150 active:scale-95 rounded-md font-medium"
          title="Permanently delete this component"
          aria-label="Delete this component"
        >
          <Trash2 size={16} className="text-red-500 flex-shrink-0" />
          <span className="flex-1">Delete Component</span>
        </button>
      </div>
    </div>
  );
};

export default memo(NodeContextMenu);
